// ===========================================
// Late fees: is this settings change STRICTER? — FRONTEND MIRROR (#372)
// ===========================================
// ⚠️ A COPY of `apps/backend/src/services/lateFees/policyChange.ts`. The server decides; this lets
// the confirm dialog say what the save will do before it is made. Both run the SAME case table
// (`apps/backend/tests/fixtures/lateFeePolicyChangeCases.json`, the agreement test), so an edit to
// one without the other fails a test. Keep the bodies identical.

import type { InvoiceSettings, LateFeeChangeKind, StricterReason } from '../types/adminSettings.types';

export type { LateFeeChangeKind, StricterReason };

export const LATE_FEE_POLICY_KEYS = [
  'lateFeeType',
  'lateFeeAmount',
  'lateFeePercent',
  'lateFeePerFeeCap',
  'lateFeeFirstDays',
  'lateFeeRepeatEnabled',
  'lateFeeRepeatFirstDays',
  'lateFeeRepeatEveryDays',
  'lateFeeMaxCount',
  'lateFeeMaxTotal',
  'lateFeeMinOwing',
  'lateFeeChargePartPaid',
  'lateFeeTaxable',
  'lateFeeChargeTuition',
  'lateFeeChargeProducts',
  'lateFeeChargeOther',
  'lateFeeLateInvoiceGapDays',
] as const satisfies ReadonlyArray<keyof InvoiceSettings>;

export type LateFeePolicyFields = Pick<InvoiceSettings, (typeof LATE_FEE_POLICY_KEYS)[number]>;

export interface LateFeePolicyChange {
  kind: LateFeeChangeKind;
  reasons: StricterReason[];
}

/** Money compared in whole cents, never float equality (15 vs 15.001 is no change). */
const cents = (v: number | null | undefined): number | null =>
  v === null || v === undefined ? null : Math.round(v * 100);

/** A limit went UP, or was removed (null = no limit). */
function limitLoosened(oldV: number | null, newV: number | null): 'HIGHER' | 'REMOVED' | null {
  if (oldV === null) return null; // there was no limit: nothing can loosen it
  if (newV === null) return 'REMOVED';
  return newV > oldV ? 'HIGHER' : null;
}

export function isStricterLateFeePolicy(
  old: LateFeePolicyFields,
  next: LateFeePolicyFields
): { stricter: boolean; reasons: StricterReason[] } {
  const reasons: StricterReason[] = [];

  if (next.lateFeeType !== old.lateFeeType) {
    reasons.push('TYPE_CHANGED');
  } else if (next.lateFeeType === 'FIXED') {
    const a = cents(old.lateFeeAmount);
    const b = cents(next.lateFeeAmount);
    if (a !== null && b !== null && b > a) reasons.push('AMOUNT_HIGHER');
  } else {
    const a = cents(old.lateFeePercent);
    const b = cents(next.lateFeePercent);
    if (a !== null && b !== null && b > a) reasons.push('PERCENT_HIGHER');
    // The per-fee cap applies to PERCENT only; while FIXED it is ignored at charge time.
    const cap = limitLoosened(cents(old.lateFeePerFeeCap), cents(next.lateFeePerFeeCap));
    if (cap === 'HIGHER') reasons.push('CAP_HIGHER');
    if (cap === 'REMOVED') reasons.push('CAP_REMOVED');
  }

  if (next.lateFeeFirstDays < old.lateFeeFirstDays) reasons.push('DAYS_FEWER');

  if (next.lateFeeRepeatEnabled && !old.lateFeeRepeatEnabled) {
    reasons.push('REPEAT_ON');
  } else if (
    next.lateFeeRepeatEnabled &&
    (next.lateFeeRepeatFirstDays < old.lateFeeRepeatFirstDays ||
      next.lateFeeRepeatEveryDays < old.lateFeeRepeatEveryDays)
  ) {
    reasons.push('REPEAT_SOONER');
  }

  const count = limitLoosened(old.lateFeeMaxCount, next.lateFeeMaxCount);
  if (count === 'HIGHER') reasons.push('MAX_COUNT_HIGHER');
  if (count === 'REMOVED') reasons.push('MAX_COUNT_REMOVED');

  const total = limitLoosened(cents(old.lateFeeMaxTotal), cents(next.lateFeeMaxTotal));
  if (total === 'HIGHER') reasons.push('MAX_TOTAL_HIGHER');
  if (total === 'REMOVED') reasons.push('MAX_TOTAL_REMOVED');

  // A minimum owing is a floor: LOWERING it (or removing it) charges more invoices.
  const a = cents(old.lateFeeMinOwing);
  const b = cents(next.lateFeeMinOwing);
  if (a !== null && b === null) reasons.push('MIN_OWING_REMOVED');
  else if (a !== null && b !== null && b < a) reasons.push('MIN_OWING_LOWER');

  if (next.lateFeeChargePartPaid && !old.lateFeeChargePartPaid) reasons.push('PART_PAID_ON');
  if (next.lateFeeTaxable && !old.lateFeeTaxable) reasons.push('TAX_ON');

  if (
    (next.lateFeeChargeTuition && !old.lateFeeChargeTuition) ||
    (next.lateFeeChargeProducts && !old.lateFeeChargeProducts) ||
    (next.lateFeeChargeOther && !old.lateFeeChargeOther)
  ) {
    reasons.push('KIND_ADDED');
  }

  if (next.lateFeeLateInvoiceGapDays < old.lateFeeLateInvoiceGapDays) reasons.push('GAP_SHORTER');

  return { stricter: reasons.length > 0, reasons };
}

/** Did any money/timing key change at all (LOOSER vs NONE)? */
export function isLateFeePolicyChanged(old: LateFeePolicyFields, next: LateFeePolicyFields): boolean {
  return LATE_FEE_POLICY_KEYS.some((key) => {
    const a = old[key];
    const b = next[key];
    if (typeof a === 'number' || typeof b === 'number') {
      return cents(a as number | null) !== cents(b as number | null);
    }
    return a !== b;
  });
}

/**
 * The ONE classifier the settings transition calls. `old.on` is the EFFECTIVE state
 * (JSON on AND the start column set), so a JSON left on by a stale write counts as OFF and the
 * next save that keeps it on re-stamps (ENABLED) instead of leaving the column null.
 */
export function classifyLateFeeChange(
  old: { on: boolean } & LateFeePolicyFields,
  next: { on: boolean } & LateFeePolicyFields
): LateFeePolicyChange {
  if (!old.on && next.on) return { kind: 'ENABLED', reasons: [] };
  if (old.on && !next.on) return { kind: 'DISABLED', reasons: [] };
  if (!old.on && !next.on) return { kind: 'NONE', reasons: [] };
  const { stricter, reasons } = isStricterLateFeePolicy(old, next);
  if (stricter) return { kind: 'STRICTER', reasons };
  return { kind: isLateFeePolicyChanged(old, next) ? 'LOOSER' : 'NONE', reasons: [] };
}
